// Gemeinsame Bausteine der Verträge der E-Rechnung (SPEZIFIKATION §10.2). Geld in Cent, Tage 'YYYY-MM-DD'.
import { zId } from '@shared/ids'
import { z } from 'zod'
import { REQUIREMENT_REASONS } from '../lib/preflight'

/** Funktionsschalter: Beim Festschreiben entsteht XML nur, wenn er an ist (§4.1). */
export const EINVOICE_FEATURE = 'einvoice'

/** Ausgehendes Lesen und Schreiben nutzt die Rechte der Finanzen (§10.2). */
export const OUTBOUND_READ = 'finance.invoice.read'
export const OUTBOUND_WRITE = 'finance.invoice.write'

export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

export const zFinding = z.object({
  code: z.string(),
  level: z.enum(['error', 'warning', 'info']),
  message: z.string(),
  /** BT/BG oder Feldpfad */
  field: z.string().nullable(),
  /** Route zum Datensatz, an dem sich der Befund beheben lässt */
  fix: z.string().nullable(),
})
export type Finding = z.output<typeof zFinding>

export const zPreflightResult = z.object({
  /** Muss beim Festschreiben eine XML entstehen? */
  required: z.boolean(),
  reason: z.enum(REQUIREMENT_REASONS),
  reasonText: z.string(),
  findings: z.array(zFinding),
  /** Festschreiben scheitert: XML nötig und mindestens ein Fehler */
  blocking: z.boolean(),
  /** Syntax und Typcode, mit denen die XML entstünde (null = keine XML) */
  syntax: z.enum(['ubl', 'cii']).nullable(),
  typeCode: z.enum(['380', '381', '384']).nullable(),
})
export type PreflightView = z.output<typeof zPreflightResult>

export const OUTBOUND_REASON_LABELS: Record<string, string> = {
  mandatory: 'gesetzliche Pflicht',
  requested: 'Wunsch des Kunden',
  public: 'öffentliche Stelle',
}
export const OUTBOUND_ORIGIN_LABELS: Record<string, string> = {
  posted: 'beim Festschreiben',
  issue: 'nachträglich ausgestellt',
}
export const TYPE_CODE_LABELS: Record<string, string> = {
  '380': 'Rechnung',
  '381': 'Rechnungskorrektur',
  '384': 'Berichtigte Rechnung',
}
export const SYNTAX_LABELS: Record<string, string> = { ubl: 'XRechnung UBL', cii: 'XRechnung CII' }

export const DELIVERY_CHANNELS = ['download', 'manual', 'portal', 'email', 'peppol'] as const
export const DELIVERY_CHANNEL_LABELS: Record<(typeof DELIVERY_CHANNELS)[number], string> = {
  download: 'Heruntergeladen',
  manual: 'Als übermittelt markiert',
  portal: 'Im Portal des Auftraggebers hochgeladen',
  email: 'Per E-Mail übermittelt (vom Mailserver angenommen)',
  peppol: 'Über Peppol versandt',
}

/** Bezeichnung einer Übermittlung mit ihrem Zustand: gescheiterte E-Mails heißen nie „übermittelt“ */
export function deliveryLabel(channel: string, status: string): string {
  if (status === 'failed') return channel === 'email' ? 'Per E-Mail nicht zugestellt' : 'Übermittlung gescheitert'
  if (status === 'queued') return channel === 'email' ? 'Per E-Mail vorgemerkt' : 'Übermittlung vorgemerkt'
  return DELIVERY_CHANNEL_LABELS[channel as (typeof DELIVERY_CHANNELS)[number]] ?? channel
}

export const zDelivery = z.object({
  id: zId,
  channel: z.enum(DELIVERY_CHANNELS),
  channelLabel: z.string(),
  recipient: z.string().nullable(),
  status: z.enum(['queued', 'done', 'failed']),
  note: z.string().nullable(),
  at: z.date(),
  byName: z.string().nullable(),
})

export const zCheck = z.object({
  tool: z.enum(['merxo', 'kosit']),
  toolVersion: z.string(),
  verdict: z.enum(['acceptable', 'rejected']),
  messages: z.array(
    z.object({ code: z.string(), level: z.string(), message: z.string(), field: z.string().nullable() }),
  ),
  createdAt: z.date(),
})

export const zOutbound = z.object({
  id: zId,
  documentId: zId,
  documentType: z.string(),
  locationId: zId,
  partnerId: zId.nullable(),
  number: z.string(),
  issueDate: z.string(),
  typeCode: z.enum(['380', '381', '384']),
  typeLabel: z.string(),
  syntax: z.enum(['ubl', 'cii']),
  syntaxLabel: z.string(),
  customizationId: z.string(),
  fileId: zId,
  fileName: z.string(),
  sha256: z.string(),
  sizeBytes: z.int(),
  grossCents: z.int(),
  taxCents: z.int(),
  payableCents: z.int(),
  /** BT-49; null, wenn das Löschkonzept ihn entfernt hat */
  buyerEndpoint: z.string().nullable(),
  reason: z.enum(['mandatory', 'requested', 'public']),
  reasonLabel: z.string(),
  origin: z.enum(['posted', 'issue']),
  originLabel: z.string(),
  writerVersion: z.string(),
  createdAt: z.date(),
  /** Inhalt nach Ablauf der Aufbewahrungsfrist entfernt – Hash, Typcode und Beträge bleiben */
  erased: z.boolean(),
  /** Inhalt der Datei liegt vor (false nach dem Löschkonzept) */
  available: z.boolean(),
  /**
   * Bezug des Belegs: Storno zu (`reversal`), Rechnungskorrektur zu (`correction`), berichtigte Rechnung zu
   * (`rectification`) – null bei einer gewöhnlichen Rechnung
   */
  reference: z
    .object({
      kind: z.enum(['reversal', 'correction', 'rectification']),
      documentId: zId,
      number: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * Der Beleg gilt nicht mehr: storniert (`reversed`, `documentId` = Storno; `ownEInvoice` = das Storno hat eine eigene
   * E-Rechnung) bzw. ersetzt durch eine berichtigte Rechnung (`replaced`, `documentId` = die berichtigte Rechnung)
   */
  superseded: z
    .object({
      kind: z.enum(['reversed', 'replaced']),
      documentId: zId,
      number: z.string().nullable(),
      ownEInvoice: z.boolean(),
    })
    .nullable()
    .optional(),
  /** Art für Menschen: „Rechnung“, „Storno zu RE-…“, „Rechnungskorrektur zu RE-…“, „Berichtigte Rechnung zu RE-…“ */
  kindLabel: z.string().optional(),
})
export type OutboundView = z.output<typeof zOutbound>
